import {
  Children,
  createElement,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

type MagnoliaComments = {
  areaStart: string;
  areaEnd: string;
  componentStart: string;
  componentEnd: string;
};

type WrapperComponent = React.ComponentType<React.PropsWithChildren>;

const COMMENT_PATTERNS = {
  AREA_START: /<!-- cms:area(.*?)-->/,
  AREA_END: /<!-- \/cms:area -->/,
  COMPONENT_START: /<!-- cms:component(.*?)-->/,
  COMPONENT_END: '<!-- /cms:component -->',
} as const;

const isMagnoliaRendererActive = () =>
  typeof window !== 'undefined' && Boolean(window.USE_MAGNOLIA_RENDERER);

const extractMagnoliaComments = (htmlString: string): MagnoliaComments => ({
  areaStart: htmlString.match(COMMENT_PATTERNS.AREA_START)?.[0] ?? '',
  areaEnd: htmlString.match(COMMENT_PATTERNS.AREA_END)?.[0] ?? '',
  componentStart: htmlString.match(COMMENT_PATTERNS.COMPONENT_START)?.[0] ?? '',
  componentEnd: COMMENT_PATTERNS.COMPONENT_END,
});

const createElementWithComments = (
  element: ReactElement,
  componentStart: string,
  componentEnd: string
): ReactElement => {
  const { props, key, type } = element;
  return createElement(type, {
    key,
    ...(props || {}),
    magnoliaComments: [componentStart, componentEnd],
  });
};

const createWrappedElement = (
  Wrapper: WrapperComponent,
  child: ReactNode,
  componentStart: string,
  componentEnd: string,
  index: number
): ReactElement => (
  <Wrapper key={index}>
    {componentStart}
    {child}
    {componentEnd}
  </Wrapper>
);

const processMagnoliaChild = (
  child: string,
  nextChild: ReactNode,
  Wrapper: WrapperComponent,
  index: number,
  withMagnoliaCommentsProp: boolean
): ReactNode[] => {
  const { areaStart, areaEnd, componentStart, componentEnd } =
    extractMagnoliaComments(child);

  const parts: ReactNode[] = [
    ...(areaStart ? [areaStart] : []),
    ...(nextChild
      ? [
          withMagnoliaCommentsProp && isValidElement(nextChild)
            ? createElementWithComments(nextChild, componentStart, componentEnd)
            : createWrappedElement(
                Wrapper,
                nextChild,
                componentStart,
                componentEnd,
                index
              ),
        ]
      : []),
    ...(areaEnd ? [componentEnd, areaEnd] : []),
  ];

  return parts;
};

const processMagnoliaChildren = (
  children: ReactNode[],
  Wrapper: WrapperComponent,
  withMagnoliaCommentsProp: boolean
): ReactNode[] => {
  return children.reduce<ReactNode[]>((acc, child, index, all) => {
    if (typeof child === 'string') {
      const nextChild = index < all.length - 1 ? all[index + 1] : null;
      return [
        ...acc,
        ...processMagnoliaChild(
          child,
          nextChild,
          Wrapper,
          index,
          withMagnoliaCommentsProp
        ),
      ];
    }
    return acc;
  }, []);
};

const processStandardChildren = (
  children: ReactNode[],
  Wrapper: WrapperComponent,
  hasOnlyWrapperInMagnolia: boolean
): ReactNode[] => {
  const WrapperComponent = hasOnlyWrapperInMagnolia ? Fragment : Wrapper;
  return children.map((child, index) => (
    <WrapperComponent key={index}>{child}</WrapperComponent>
  ));
};

const childrenArrayWrapper = (
  children: ReactNode,
  Wrapper: WrapperComponent,
  hasOnlyWrapperInMagnolia = false,
  withMagnoliaCommentsProp = false
): ReactNode[] => {
  const childrenArray = Children.toArray(children);

  return isMagnoliaRendererActive()
    ? processMagnoliaChildren(childrenArray, Wrapper, withMagnoliaCommentsProp)
    : processStandardChildren(childrenArray, Wrapper, hasOnlyWrapperInMagnolia);
};

export default childrenArrayWrapper;
